import { getText } from "shared/lib";
import {
  TreePropertyType,
  updateTreeProperty,
  type DistanceTreePropertyDescriptor,
  type TreeDescriptor,
} from "../lib";
import { useTreeDialogValidator } from "./lib";

import { useIsRTL } from "@/hooks";

import { Input2, Slider } from "shared/components";
import { FieldLabel } from "./FieldLabel";
import { FieldValidationMessages } from "./FieldValidationMessages";

export interface DistanceFieldProps {
  label?: string;
  onChange: ((treeDescriptor: TreeDescriptor) => void) | undefined;
  treeDescriptor: TreeDescriptor;
}

export function DistanceField<
  T extends DistanceTreePropertyDescriptor = DistanceTreePropertyDescriptor,
>({ label, onChange, treeDescriptor }: DistanceFieldProps) {
  const isRTL = useIsRTL();

  const { isValid, messages } = useTreeDialogValidator<T>({
    checker: (property) => !!property?.value,
    properties: treeDescriptor.properties,
    treePropertyType: TreePropertyType.distance,
  });

  const distance = treeDescriptor.properties.get(TreePropertyType.distance) as
    | T
    | undefined;

  const handleChange = () => (updater: (draft: T) => void) => {
    const newTreeDescriptor = updateTreeProperty<T>(
      treeDescriptor,
      TreePropertyType.distance,
      updater,
    );

    onChange?.(newTreeDescriptor);
  };

  return (
    <div className="flex flex-col gap-2">
      <FieldLabel isValid={isValid}>
        {label ? label : getText("Distance from works")}
      </FieldLabel>

      <div className="flex items-center gap-2">
        <Slider
          defaultValue={[2]}
          max={15}
          min={0}
          step={1}
          onValueChange={(value) => {
            handleChange()((draft) => {
              draft.value = parseFloat(value[0].toString())
                .toFixed(2)
                .toString();
            });
          }}
          value={[distance?.value ? parseFloat(distance.value) : 0]}
        />

        <Input2
          className="text-bodymd placeholder:text-bodymd w-15 min-w-15 not-rtl:pl-1 not-rtl:pr-0 relative h-5 pr-0 rtl:pl-0 rtl:pr-1"
          {...(isRTL
            ? {
                startClassName:
                  "border border-interactiveBorder bg-interfaceBgDarkPaper absolute left-[-1px] top-[-1px] bottom-[-1px] px-1 rounded-l-sm text-bodymd",
                startContent: "m",
              }
            : {
                endClassName:
                  "border border-interactiveBorder bg-interfaceBgDarkPaper absolute right-[-1px] top-[-1px] bottom-[-1px] px-1 rounded-r-sm text-bodymd",
                endContent: "m",
              })}
          max={15}
          min={0}
          step={1}
          onChange={(event) => {
            handleChange()((draft) => {
              draft.value = event.target.value;
            });
          }}
          rounded="sm"
          type="number"
          value={distance?.value || ""}
        />
      </div>

      <FieldValidationMessages messages={messages} />
    </div>
  );
}
